import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Building2, Check, ChevronsUpDown, Cog, FolderTree, Link2, Plus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth.ts';
import { groupByOrg, useAvailableContexts } from '@/hooks/useContexts.ts';
import { useActivateContext } from '@/hooks/useActivateContext.ts';
import { CreateOrganizationDialog } from '@/components/shell/CreateOrganizationDialog.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command.tsx';
import { cn } from '@/lib/utils.ts';

/**
 * Append `?ctx=<id>` to the current URL and copy it to the clipboard.
 * Shared/email links carry the context explicitly so the recipient (or
 * you in another tab) lands in the right org/division regardless of
 * whatever context their session last had — the `?ctx=` deep-link
 * handler auto-switches on open. In-app navigation stays param-free.
 */
async function copyShareableLink(contextId: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('ctx', contextId);
  try {
    await navigator.clipboard.writeText(url.toString());
    toast.success('Link copied — opens in this context');
  } catch {
    toast.error('Could not copy link to clipboard');
  }
}

/**
 * ContextSwitcher — replaces the legacy Bulma navbar dropdown.
 *
 * Spec §9.7: MSP techs may have 50+ contexts (one per customer
 * division). A flat dropdown doesn't scale; this composes shadcn
 * Popover + Command for fuzzy-search keyed on context name.
 *
 * Active context is the one the backend persisted as
 * `user.activeContext`. Switching POSTs to `/api/contexts/:id/activate`,
 * then invalidates every query (the whole UI is context-scoped) and
 * sets the local active-context state so subsequent requests carry the
 * new context header.
 *
 * Contexts are grouped by organization (headers shown only when the user
 * belongs to more than one org), and the footer carries a "Create
 * organization" action — every user gets a place they own, gated to one
 * owned org each.
 *
 * Rendered inside the desktop TopBar today. The mobile placement (in
 * the drawer header) lands when SideNav grows a context slot — Phase 2
 * primitive iteration.
 */
export function ContextSwitcher() {
  const { user } = useAuth();
  const { data: contexts, isLoading } = useAvailableContexts(user?.organizationId);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const activate = useActivateContext();

  if (!user?.organizationId) return null;

  // `contexts` arrives sorted, so this fallback names the organization context of
  // the first organization rather than whichever row the API happened to return.
  const activeContext = user.activeContext ?? contexts?.[0];
  const activeContextId = user.activeContext?.id;

  const orgNameById = new Map(user.organizations.map((o) => [o.id, o.name]));
  const groups = groupByOrg(contexts ?? [], orgNameById);
  const showOrgHeadings = groups.length > 1;

  // The cap counts free organizations only, so ownership alone does not decide
  // this — the server publishes the answer.
  const ownsOrg = user.canCreateOrganization === false;

  const label = isLoading
    ? 'Loading…'
    : activeContext
      ? activeContext.name
      : 'No context';
  // Show the active context's organization next to its name — two orgs can each
  // have an "Org Admin" context, so the org is what disambiguates the selection.
  const activeOrgName =
    activeContext && activeContext.organizationId
      ? orgNameById.get(activeContext.organizationId) ?? null
      : null;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 max-w-xs text-sm px-2.5 py-1.5 rounded-md hover:bg-accent transition-colors"
            aria-label="Switch context"
          >
            <div className="w-5 h-5 rounded bg-brand-soft flex items-center justify-center text-brand text-xs font-semibold flex-shrink-0">
              {activeContext?.name.charAt(0).toUpperCase() ?? '·'}
            </div>
            <span className="font-medium truncate">{label}</span>
            {activeOrgName ? (
              <span className="text-muted-foreground text-xs hidden lg:inline truncate">/ {activeOrgName}</span>
            ) : null}
            <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command>
            <CommandInput placeholder="Search contexts…" />
            <CommandList>
              <CommandEmpty>No matching context.</CommandEmpty>
              {groups.map((g) => (
                <CommandGroup key={g.orgId} heading={showOrgHeadings ? g.orgName : undefined}>
                  {g.contexts.map((c) => {
                    const isActive = activeContextId === c.id;
                    return (
                      <CommandItem
                        key={c.id}
                        // Searchable by division too: in an MSP's list the customer
                        // is what somebody types, and a renamed context need not
                        // contain it.
                        value={`${c.name} ${c.divisionName ?? ''} ${g.orgName} ${c.divisionId ? 'division' : 'organization'}`}
                        onSelect={() => {
                          if (!isActive) {
                            activate.mutate(c.id, { onSuccess: () => { setOpen(false); } });
                          } else {
                            setOpen(false);
                          }
                        }}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        {c.divisionId ? (
                          <FolderTree className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        )}
                        <span className="flex-1 truncate">{c.name}</span>
                        {/* The division's name rather than a constant "DIV": with the
                            list ordered by division it labels which block a row
                            belongs to, and it is the half that disambiguates two
                            similarly-named contexts. Organization-level rows keep the
                            chip — there is no name to show, and it marks the widest
                            view in the list. */}
                        {c.divisionId ? (
                          <span className="text-[11px] text-muted-foreground truncate max-w-[8rem]">
                            {c.divisionName ?? 'Division'}
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">org</span>
                        )}
                        <Check
                          className={cn(
                            'w-3.5 h-3.5 ml-1 text-brand',
                            isActive ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
              <CommandSeparator />
              <CommandGroup>
                {ownsOrg ? (
                  <CommandItem
                    disabled
                    className="flex items-center gap-2 text-muted-foreground"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span className="flex-1">Create organization</span>
                    <span className="text-[10px] text-muted-foreground truncate max-w-[7rem]">
                      You already own a free one
                    </span>
                  </CommandItem>
                ) : (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setCreateOpen(true);
                    }}
                    className="flex items-center gap-2 cursor-pointer text-muted-foreground"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Create organization</span>
                  </CommandItem>
                )}
                {activeContextId ? (
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      void copyShareableLink(activeContextId);
                    }}
                    className="flex items-center gap-2 cursor-pointer text-muted-foreground"
                  >
                    <Link2 className="w-3.5 h-3.5" />
                    <span>Copy link to this view</span>
                  </CommandItem>
                ) : null}
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    void navigate('/admin/contexts');
                  }}
                  className="flex items-center gap-2 cursor-pointer text-muted-foreground"
                >
                  <Cog className="w-3.5 h-3.5" />
                  <span>Manage contexts</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <CreateOrganizationDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}
